// PAM (SSH) sections for the shared policy screens: Where (gateway, hosts,
// accounts, sudo), the Allow lock under Action, the grants on the detail
// page, and the list's old-link forwarding.
import React, { useEffect, useMemo, useState } from "react";
import { Input, InputNumber, Radio, Select, Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

import { AN_COLORS, AN_MONO } from "../../../../design-system/tokens";
import {
  getAdminCredentialStatus,
  listEndpointAccounts,
  listGrants,
  listInstances,
  listJumpServer,
  listSSHConnections,
  endpointLifecycle,
} from "../../../../service/sshProxyService";
import GrantsPanel from "../../../enduser/ssh-proxy/policy/GrantsPanel";
import { Field, Muted, Note, TextButton } from "../../ui";
import { fieldDomId } from "../../editor/draft";
import { policiesPath } from "../../policyProducts";
import {
  SUDO_MAX_MINUTES,
  SUDO_MIN_MINUTES,
  SUDO_MODES,
  TEMP_ACCOUNT,
  cleanList,
  endpointAddress,
  endpointForHost,
  endpointLabel,
  isNoKeyAccount,
} from "./sshModel";
import { loadConsoleUsers } from "./directory";

/* ------------------------------------------------------------------- data */

/** Gateways and onboarded endpoints, for the list, detail and editor pages. */
export function useSshData(draft) {
  const [gateways, setGateways] = useState(null);
  const [instances, setInstances] = useState(null);
  useEffect(() => {
    let live = true;
    listJumpServer({
      payload: { pageSize: 200 },
      onSuccess: (d) => live && setGateways(d?.jumpServers || []),
      onFailure: () => live && setGateways([]),
    });
    listInstances({
      onSuccess: (rows) => live && setInstances(Array.isArray(rows) ? rows : []),
      onFailure: () => live && setInstances([]),
    });
    return () => {
      live = false;
    };
  }, []);
  return {
    gateways: gateways || [],
    instances: instances || [],
    instancesLoaded: instances !== null,
    // The list and detail pages call this with no draft; the editor with one.
    page: draft ? "editor" : "view",
  };
}

export const gatewayName = (data, id) => {
  const g = (data?.gateways || []).find((x) => String(x.id) === String(id));
  return g?.serverName || `Gateway ${id}`;
};

// Hosts are onboarded endpoints with a gateway (not decommissioned), each
// labelled with the gateway it is reached through; only the picked gateway's
// when one is picked.
export const hostOptions = (data, gatewayId) =>
  (data?.instances || [])
    .filter((r) => r.jumpServerId && !endpointLifecycle(r.status) && (!gatewayId || String(r.jumpServerId) === String(gatewayId)))
    .map((r) => ({ value: endpointAddress(r), label: `${endpointLabel(r)} · via ${gatewayName(data, r.jumpServerId)}` }))
    .filter((o) => o.value);

/* ---------------------------------------------------------------- gateway */

export function GatewayField({ product, setProduct, data }) {
  const value = (product.jumpServers || [])[0];
  const options = (data?.gateways || []).map((g) => ({ value: Number(g.id), label: g.serverName || `Gateway ${g.id}` }));
  return (
    <Field label="Gateway" hint="optional" help="Leave empty and each host is reached through its own gateway." htmlFor={fieldDomId("where.gateway")}>
      <Select
        id={fieldDomId("where.gateway")}
        aria-label="Gateway"
        allowClear
        showSearch
        optionFilterProp="label"
        value={value != null ? Number(value) : undefined}
        placeholder="Any gateway"
        options={options}
        onChange={(v) => setProduct({ jumpServers: v != null ? [v] : [] })}
        style={{ width: "100%" }}
      />
    </Field>
  );
}

/* ------------------------------------------------------------------ where */

// The accounts scanned on the picked endpoints (their union) and each
// endpoint's admin account. Typing a name still works.
function useAccountSuggestions(endpointIds) {
  const key = endpointIds.join(",");
  const [state, setState] = useState({ loading: false, scanned: [], admins: [] });
  useEffect(() => {
    const ids = key ? key.split(",").map(Number) : [];
    if (!ids.length) {
      setState({ loading: false, scanned: [], admins: [] });
      return undefined;
    }
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    const scanned = new Promise((resolve) =>
      listEndpointAccounts({
        instanceIds: ids,
        onSuccess: (rows) => {
          const names = [];
          (rows || []).forEach((r) => (r.accounts || []).forEach((a) => a?.name && !names.includes(a.name) && names.push(a.name)));
          resolve(names);
        },
        onFailure: () => resolve([]),
      }),
    );
    const admins = Promise.all(
      ids.map(
        (instanceId) =>
          new Promise((resolve) =>
            getAdminCredentialStatus({
              instanceId,
              onSuccess: (d) => resolve(d?.mode === "admin" && d?.username ? d.username : null),
              onFailure: () => resolve(null),
            }),
          ),
      ),
    );
    Promise.all([scanned, admins]).then(([names, adminNames]) => {
      if (live) setState({ loading: false, scanned: names, admins: [...new Set(adminNames.filter(Boolean))] });
    });
    return () => {
      live = false;
    };
  }, [key]);
  return state;
}

// Only one grant per account per host can exist: publishing a policy for an
// account another policy holds on a host moves that grant here. Said before
// publishing, not after.
function useTakenGrants(endpoints, accounts, ownId) {
  const key = `${endpoints.map((r) => r.instanceId).join(",")}|${accounts.join(",")}|${ownId || ""}`;
  const [taken, setTaken] = useState([]);
  useEffect(() => {
    if (!endpoints.length || !accounts.length) {
      setTaken([]);
      return undefined;
    }
    let live = true;
    Promise.all(
      endpoints.map(
        (r) =>
          new Promise((resolve) =>
            listGrants({ instanceId: r.instanceId, onSuccess: (g) => resolve((g || []).map((x) => ({ ...x, endpoint: r }))), onFailure: () => resolve([]) }),
          ),
      ),
    ).then((lists) => {
      if (!live) return;
      setTaken(
        lists
          .flat()
          .filter((g) => (g.status === "active" || g.status === "pending") && accounts.includes(g.account) && (!ownId || String(g.policyId) !== String(ownId))),
      );
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return taken;
}

const hostMatches = (h, address) => h === address || String(h).split(":")[0] === address;

export function Where(props) {
  const { draft, product, setProduct, errors, touch, data, isEdit } = props;
  const gatewayId = (product.jumpServers || [])[0];
  const hosts = useMemo(() => product.hosts || [], [product.hosts]);
  const accounts = product.accounts || [];
  const options = useMemo(() => hostOptions(data, gatewayId), [data, gatewayId]);

  // A host typed as address:port still belongs to the endpoint at that address.
  const picked = useMemo(() => {
    const seen = new Map();
    hosts.forEach((h) => {
      const ep = endpointForHost(data?.instances, h);
      if (ep && ep.jumpServerId) seen.set(ep.instanceId, ep);
    });
    return [...seen.values()];
  }, [hosts, data]);
  const pickedIds = picked.map((r) => r.instanceId).filter(Boolean).sort((a, b) => a - b);
  const suggestions = useAccountSuggestions(pickedIds);
  const taken = useTakenGrants(picked, accounts, isEdit ? product.policyId : null);

  const offered = [...new Set([...suggestions.scanned, ...suggestions.admins])].filter((n) => n !== TEMP_ACCOUNT);
  const accountOptions = [
    ...offered.map((n) => ({
      value: n,
      label: (
        <span>
          <span style={{ fontFamily: AN_MONO }}>{n}</span>
          {suggestions.admins.includes(n) && <span style={{ color: AN_COLORS.muted }}> · admin account</span>}
        </span>
      ),
      disabled: isNoKeyAccount(n),
    })),
    ...accounts
      .filter((a) => a !== TEMP_ACCOUNT && !offered.includes(a))
      .map((n) => ({
        value: n,
        label: (
          <span style={{ color: AN_COLORS.muted }}>
            <span style={{ fontFamily: AN_MONO }}>{n}</span>
            {pickedIds.length ? " · not found on the picked hosts" : ""}
          </span>
        ),
      })),
    { value: TEMP_ACCOUNT, label: "Temporary account per session" },
  ];

  // Problems the backend would skip silently, in one note.
  const problems = [];
  hosts.forEach((h) => {
    const ep = endpointForHost(data?.instances, h);
    if (!data?.instancesLoaded) return;
    if (!ep) problems.push(`${h} isn't an onboarded endpoint, so it gets no grants.`);
    else if (!ep.jumpServerId) problems.push(`${h} has no gateway, so it gets no grants.`);
    else if (gatewayId && String(ep.jumpServerId) !== String(gatewayId)) problems.push(`${h} is reached through ${gatewayName(data, ep.jumpServerId)}, not the gateway picked above.`);
  });
  const noKey = accounts.filter(isNoKeyAccount);
  if (noKey.length) problems.push(`${noKey.join(", ")} never ${noKey.length === 1 ? "gets a key" : "get keys"}, so sessions to ${noKey.length === 1 ? "it" : "them"} are refused.`);
  taken.forEach((g) =>
    problems.push(`${g.account} on ${g.endpoint.hostName || endpointAddress(g.endpoint)} is held by “${g.policyName || "another policy"}”. Saving moves it here.`),
  );
  (data?.gateways || [])
    .filter((g) => String(g.status || "").toUpperCase() === "DISABLED" && picked.some((r) => String(r.jumpServerId) === String(g.id)))
    .forEach((g) => problems.push(`${g.serverName || "A gateway"} is off, so no session to the hosts behind it opens until it's switched on.`));

  const sudoMode = product.sudoMode || "none";

  return (
    <>
      <GatewayField {...props} />
      <Field label="Hosts" required error={errors["where.hosts"]} htmlFor={fieldDomId("where.hosts")}>
        <Select
          id={fieldDomId("where.hosts")}
          aria-label="Hosts"
          mode="multiple"
          value={hosts}
          onChange={(v) => setProduct({ hosts: cleanList(v) })}
          onBlur={() => touch("where.hosts")}
          options={options}
          optionFilterProp="label"
          status={errors["where.hosts"] ? "error" : undefined}
          placeholder={options.length ? "Pick onboarded endpoints" : data?.instancesLoaded ? "No onboarded endpoints with a gateway yet" : "Loading endpoints…"}
          notFoundContent={data?.instancesLoaded ? "No matching endpoints" : <Spin size="small" />}
          style={{ width: "100%" }}
        />
      </Field>

      <Field
        label="Accounts"
        required
        error={errors["where.accounts"]}
        help="Each account gets its own grant on each host. Type any name the hosts haven't reported yet."
        htmlFor={fieldDomId("where.accounts")}
      >
        <Select
          id={fieldDomId("where.accounts")}
          aria-label="Accounts"
          mode="tags"
          value={accounts}
          onChange={(v) => setProduct({ accounts: cleanList(v) })}
          onBlur={() => touch("where.accounts")}
          options={accountOptions}
          optionFilterProp="value"
          loading={suggestions.loading}
          tokenSeparators={[",", " "]}
          status={errors["where.accounts"] ? "error" : undefined}
          placeholder={offered.length ? `e.g. ${offered.slice(0, 2).join(", ")}` : "ubuntu"}
          style={{ width: "100%", fontFamily: AN_MONO }}
        />
      </Field>

      <Field label="Sudo" help={sudoMode === "none" ? "Sessions keep the account's own rights." : null}>
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <Radio.Group value={sudoMode} onChange={(e) => setProduct({ sudoMode: e.target.value })} aria-label="Sudo">
            {SUDO_MODES.map((m) => (
              <Radio key={m.value} value={m.value}>
                {m.label}
              </Radio>
            ))}
          </Radio.Group>
          {sudoMode === "commands" && (
            <Field label="Commands" required error={errors["where.sudo"]} help="One full path per line, with arguments if needed. A bare path allows any arguments." htmlFor={fieldDomId("where.sudo")}>
              <Input.TextArea
                id={fieldDomId("where.sudo")}
                aria-label="Commands"
                value={product.sudoCommands || ""}
                onChange={(e) => setProduct({ sudoCommands: e.target.value })}
                onBlur={() => touch("where.sudo")}
                autoSize={{ minRows: 3, maxRows: 10 }}
                placeholder={"/usr/bin/systemctl restart nginx\n/usr/bin/journalctl"}
                status={errors["where.sudo"] ? "error" : undefined}
                spellCheck={false}
                style={{ fontFamily: AN_MONO }}
              />
            </Field>
          )}
          {sudoMode !== "none" && (
            <Field
              label="Time limit"
              required
              error={errors["where.sudoMinutes"]}
              help={draft.action === "mfa_required" ? "Only for sessions approved with MFA. Removed when the session ends." : "Only sessions approved with MFA are elevated. Choose Require MFA under Action for this to apply."}
              htmlFor={fieldDomId("where.sudoMinutes")}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                <InputNumber
                  id={fieldDomId("where.sudoMinutes")}
                  aria-label="Time limit"
                  min={SUDO_MIN_MINUTES}
                  max={SUDO_MAX_MINUTES}
                  precision={0}
                  value={product.sudoMinutes}
                  onChange={(v) => setProduct({ sudoMinutes: v ?? null })}
                  onBlur={() => touch("where.sudoMinutes")}
                  status={errors["where.sudoMinutes"] ? "error" : undefined}
                  style={{ width: "7rem" }}
                />
                <Muted>min</Muted>
              </span>
            </Field>
          )}
        </div>
      </Field>

      {problems.length > 0 && (
        <Note tone="warn">
          {problems.length === 1 ? (
            problems[0]
          ) : (
            <ul style={{ margin: 0, paddingLeft: "1rem" }}>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </Note>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- action */

// Allow (no second factor) is for administrators. A stored Allow policy keeps
// its action; validate() raises "action.flow", shown here.
export function ActionExtra({ errors }) {
  const msg = errors["action.flow"];
  if (!msg) return null;
  return (
    <div id={fieldDomId("action.flow")} role="alert" style={{ fontSize: "0.75rem", color: AN_COLORS.danger, scrollMarginTop: "1rem" }}>
      {msg}
    </div>
  );
}

/* ----------------------------------------------------------------- grants */

// "What did I just grant?": each user's connect commands, as the server
// renders them. For Everyone, the signed-in admin's own (userId 0 = me).
function ConnectCommands({ draft, data }) {
  const [rows, setRows] = useState(null);
  const p = draft.product;
  useEffect(() => {
    let live = true;
    // With no gateway named, the backend lists each host through every
    // gateway; only the endpoint's own gateway reaches it.
    const ownGateway = (c) => {
      const ep = endpointForHost(data?.instances, c.host);
      return !ep || Number(c.jumpServerId) === Number(ep.jumpServerId);
    };
    const relevant = (c) =>
      ownGateway(c) &&
      (!p.hosts.length || p.hosts.some((h) => hostMatches(h, c.host))) && (!p.accounts.length || p.accounts.includes(c.account));
    loadConsoleUsers()
      .catch(() => [])
      .then((users) => {
        const targets = draft.who.everyone
          ? [{ userId: 0, email: "Your account (everyone gets the same format)" }]
          : (draft.who.users || []).map((email) => ({ email, userId: users.find((u) => u.email === email)?.userId }));
        if (!live) return;
        setRows(targets.map((t) => ({ ...t, loading: true })));
        targets.forEach((t, i) => {
          const done = (entry) => live && setRows((r) => (r ? r.map((x, j) => (j === i ? { ...x, ...entry, loading: false } : x)) : r));
          if (t.userId === undefined) return done({ error: "No console user has this email." });
          return listSSHConnections({
            userId: t.userId,
            onSuccess: (d) => done({ connections: (d.connections || []).filter(relevant), message: d.message }),
            onFailure: () => done({ error: "Couldn't load connect commands." }),
          });
        });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!rows) return <Spin size="small" />;
  if (!rows.length) return <Muted>No named users. Groups get connect commands once their members sign in.</Muted>;
  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      {rows.map((r) => (
        <div key={r.email} style={{ display: "grid", gap: "0.25rem" }}>
          <div style={{ fontWeight: 500, color: AN_COLORS.ink }}>{r.email}</div>
          {r.loading ? (
            <Spin size="small" />
          ) : r.error ? (
            <Muted>{r.error}</Muted>
          ) : !(r.connections || []).length ? (
            <Muted>{r.message || "No connect commands yet."}</Muted>
          ) : (
            r.connections.map((c) => (
              <div key={`${c.host}|${c.account}|${c.command}`} style={{ fontFamily: AN_MONO, fontSize: "0.8125rem", overflowWrap: "anywhere" }}>
                {c.command}
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  );
}

/** The detail page's Grants row: the keys this policy holds, refreshing after a publish. */
export function GrantsSummary({ draft, data }) {
  const location = useLocation();
  const justSaved = !!location.state?.toast;
  const [showCommands, setShowCommands] = useState(justSaved);
  const endpointName = (id) => {
    const r = (data?.instances || []).find((x) => String(x.instanceId) === String(id));
    return r ? endpointLabel(r) : `Endpoint ${id}`;
  };
  if (draft.action === "block") return <Muted>Deny policies hold no grants.</Muted>;
  return (
    <div style={{ display: "grid", gap: "0.75rem", minWidth: 0 }}>
      <GrantsPanel policyId={draft.product.policyId} endpointName={endpointName} canSync compact justSaved={justSaved} />
      {showCommands ? (
        <ConnectCommands draft={draft} data={data} />
      ) : (
        <div>
          <TextButton onClick={() => setShowCommands(true)}>Show connect commands</TextButton>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- list */

// Old links land on the list: ?host= (endpoint onboarding's "write a policy")
// opens a new policy with that host; ?tab=password-rotation goes to Endpoint
// Users, where rotation now lives.
export function ListRedirects() {
  const { search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(search);
    if (q.get("tab") === "password-rotation") navigate("/pam/endpointsUsers", { replace: true });
    else if (q.get("host")) navigate(`${policiesPath("ssh", "new")}?host=${encodeURIComponent(q.get("host"))}`, { replace: true });
  }, [search, navigate]);
  return null;
}
